import { Head, Link, router, usePage } from '@inertiajs/react'
import { Pencil, Search, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { editInvestmentPath, investmentsPath } from '@/lib/routes'
import { useDebouncedCallback } from '@/lib/useDebouncedCallback'
import type { PaginationMeta, SharedPageProps } from '@/types/inertia'

type InvestmentRow = {
  id: number
  date: string
  amount_invested: number
  investment_type: 'safe' | 'convertible_note' | 'priced_equity'
  stage_at_investment: string | null
  fund: { id: number; name: string; slug: string }
  company: { id: number; name: string; slug: string }
  has_downstream: boolean
}

type Props = {
  investments: InvestmentRow[]
  pagination: PaginationMeta
  filters: { q: string | null }
}

const TYPE_LABEL = {
  safe: 'SAFE',
  convertible_note: 'Note',
  priced_equity: 'Equity',
} as const

const PARTIAL_KEYS = ['investments', 'pagination', 'filters']

function visitWith(q: string | null) {
  router.get(
    investmentsPath(),
    q ? { q } : {},
    { preserveState: true, preserveScroll: true, replace: true, only: PARTIAL_KEYS },
  )
}

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

function InvestmentsIndex({ investments, pagination, filters }: Props) {
  const { auth } = usePage<SharedPageProps>().props
  const isPartner = auth.user?.role === 'partner'
  const [query, setQuery] = useState(filters.q ?? '')

  useEffect(() => {
    setQuery(filters.q ?? '')
  }, [filters.q])

  const searchDebounced = useDebouncedCallback((next: string) => {
    visitWith(next || null)
  }, 300)

  const onChange = (value: string) => {
    setQuery(value)
    searchDebounced(value)
  }

  const clear = () => {
    setQuery('')
    searchDebounced.cancel()
    visitWith(null)
  }

  return (
    <>
      <Head title="Investments" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <PageHeader
          title="Investments"
          description="Every check a fund has written — SAFEs, notes, and priced rounds."
        />

        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-12 pl-11 pr-12 text-base"
            placeholder="Search by company name…"
            value={query}
            onChange={(e) => onChange(e.target.value)}
          />
          {query && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mb-3 flex items-center justify-end text-xs text-muted-foreground">
          <span className="tabular-nums">{pagination.total_count} total</span>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3 font-medium">Company</th>
                  <th className="px-6 py-3 font-medium">Fund</th>
                  <th className="px-6 py-3 font-medium">Type</th>
                  <th className="px-6 py-3 font-medium text-right">Amount</th>
                  <th className="px-6 py-3 font-medium">Stage</th>
                  {isPartner && <th className="w-16 px-6 py-3 font-medium" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {investments.length === 0 && (
                  <tr>
                    <td
                      colSpan={isPartner ? 7 : 6}
                      className="px-6 py-10 text-center text-muted-foreground"
                    >
                      {query ? 'No investments match that search.' : 'No investments yet.'}
                    </td>
                  </tr>
                )}
                {investments.map((i) => (
                  <tr key={i.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 text-muted-foreground tabular-nums">{i.date}</td>
                    <td className="px-6 py-4 font-medium">{i.company.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{i.fund.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {TYPE_LABEL[i.investment_type]}
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums">
                      {formatMoney(i.amount_invested)}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {i.stage_at_investment ?? '—'}
                    </td>
                    {isPartner && (
                      <td className="px-6 py-4 text-right">
                        <Link href={editInvestmentPath(i.id)} aria-label="Edit">
                          <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={investmentsPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
            query={query ? { q: query } : {}}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(InvestmentsIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
